import type { ChartV2Series } from '@chartv2';

import { captureChartV2Image } from './captureChartV2Image';
import { hasExportableData } from './chartV2XlsxData';

const FALLBACK_FILE_NAME = 'chart';
const XLSX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const sanitizeFileName = (fileName: string): string => {
  const cleaned = fileName.replace(/[\\/:*?"<>|]+/g, '-').trim();

  return cleaned.length > 0 ? cleaned : FALLBACK_FILE_NAME;
};

const triggerDownload = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${sanitizeFileName(fileName)}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export interface DownloadChartV2XlsxParams {
  series: ChartV2Series[];
  title: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  chartNode: HTMLElement | null;
}

/**
 * Builds an `.xlsx` (styled data table + a PNG snapshot of the chart) using
 * ExcelJS and triggers a browser download. ExcelJS is imported lazily so it
 * stays out of the main bundle until a user actually downloads. Returns
 * `false` when there is no data to export.
 */
export const downloadChartV2Xlsx = async ({
  series,
  title,
  xAxisLabel,
  yAxisLabel,
  chartNode,
}: DownloadChartV2XlsxParams): Promise<boolean> => {
  if (!hasExportableData(series)) {
    return false;
  }

  const image = captureChartV2Image(chartNode, {
    title,
    xAxisLabel,
    yAxisLabel,
  });
  const { buildChartV2Workbook } = await import('./buildChartV2Workbook');
  const workbook = buildChartV2Workbook({ series, title, image });
  const buffer = await workbook.xlsx.writeBuffer();

  triggerDownload(new Blob([buffer], { type: XLSX_MIME_TYPE }), title);

  return true;
};
